import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { useMediaQuery } from 'react-responsive';
import { Outlet } from 'react-router-dom';

import {
  useConsumerQuery,
  useConsumerStatusSummaryQuery,
  useFeaturesQuery,
} from 'apollo/queries/autogenerate/hooks';
import eventBus from 'shared/utils/event-bus';
import Mixpanel from 'shared/vendor/Mixpanel';
import Signout from 'web/modules/auth/organisms/Signout';
import Loading from 'web/modules/common/components/molecules/Loading';
import AppDownload from 'web/modules/common/components/organisms/AppDownload';
import Navigation from 'web/modules/common/components/organisms/Navigation';

const QUERY_OPTIONS: any = {
  fetchPolicy: 'network-only',
  nextFetchPolicy: 'cache-only',
};

const RootScreen = () => {
  const isBigScreen = useMediaQuery({ minWidth: 768 });
  const [isLoading, setIsLoading] = useState(true);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const [openSignout, setOpenSignout] = useState(false);
  const [openAppDownload, setOpenAppDownload] = useState(false);

  /**
   * Load all necessary queries here and these are cached afterwards.
   * Prevents the page from flickering because we hide everything behind the loading screen
   */
  const { data: consumerData, loading: consumerLoading } = useConsumerQuery(
    QUERY_OPTIONS
  );
  const { loading: consumerStatusLoading } = useConsumerStatusSummaryQuery(
    QUERY_OPTIONS
  );
  const { loading: featuresLoading } = useFeaturesQuery(QUERY_OPTIONS);

  useEffect(() => {
    if (!consumerLoading && !consumerStatusLoading && !featuresLoading) {
      setIsLoading(false);

      if (consumerData) {
        const consumerHash = consumerData.consumer.externalConsumerIds.tracking;

        Mixpanel.identify(consumerHash);
        Mixpanel.setUserProperties({
          'Consumer Hash': consumerHash,
        });
      }
    }
  }, [consumerLoading, consumerStatusLoading, featuresLoading, consumerData]);

  useEffect(() => {
    eventBus.on('signout:open', () => setOpenSignout(true));
    eventBus.on('appdl:open', () => setOpenAppDownload(true));
  }, []);

  return (
    <View
      style={[
        style.root,
        isBigScreen && style.rootBigScreen,
        !isLoading && isAnimationComplete && style.dataLoaded,
      ]}
    >
      {!isLoading && isAnimationComplete && (
        <>
          <Navigation />
          <View style={[style.main, isBigScreen && style.mainBigScreen]}>
            <Outlet />
          </View>
        </>
      )}
      <Loading
        open={isLoading}
        onComplete={() => setIsAnimationComplete(true)}
      />
      <AppDownload
        open={openAppDownload}
        onCancel={() => setOpenAppDownload(false)}
      />
      <Signout open={openSignout} onCancel={() => setOpenSignout(false)} />
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    flex: 1,
    position: 'relative',
    flexDirection: 'column-reverse',
    minWidth: 320,
  },
  rootBigScreen: {
    flexDirection: 'row',
  },
  dataLoaded: {
    backgroundColor: '#EEF0F2',
  },
  main: {
    flex: 1,
  },
  mainBigScreen: {
    padding: 0,
    marginLeft: 48,
  },
});

export default RootScreen;
